/**
 * AboutRJBDModal.tsx
 * Modal displaying information about Partner Nest with beautiful design
 */

import React, { useEffect } from 'react';
import { X, Heart, Users, Shield, Star, Award, CheckCircle } from 'lucide-react';
import { trackModalView } from '../utils/visitorTracking';

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function AboutRJBDModal({ open, onClose }: Props) {
  // Track modal view when opened
  useEffect(() => {
    if (open) {
      trackModalView('About RJBD Modal');
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0b3b82]/70 backdrop-blur-lg"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0b3b82]/95 border border-white/10 rounded-3xl shadow-[0_45px_120px_-30px_rgba(255,45,135,0.35)]"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundImage: `
            url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fce7f3' fill-opacity='0.2'%3E%3Cpath d='M30 30c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10-10-4.477-10-10zm-20 0c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10-10-4.477-10-10z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
          `,
          backgroundSize: 'cover',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white shadow-lg transition-all z-10"
          aria-label="Close modal"
        >
          <X className="w-5 h-5 text-gray-700" />
        </button>

        {/* Header with Gradient */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b3b82] via-[#5f1c7c] to-[#ff2d87] opacity-95"></div>
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 0C22.4 0 0 22.4 0 50s22.4 50 50 50 50-22.4 50-50S77.6 0 50 0zm0 90c-22.1 0-40-17.9-40-40s17.9-40 40-40 40 17.9 40 40-17.9 40-40 40z' fill='%23ffffff' fill-opacity='1'/%3E%3Ccircle cx='50' cy='35' r='8' fill='%23ffffff'/%3E%3Cpath d='M50 48c-8.8 0-16 5.4-16 12v10h32V60c0-6.6-7.2-12-16-12z' fill='%23ffffff'/%3E%3C/svg%3E")`,
            }}
          ></div>
          <div className="relative px-8 py-4 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 mb-2 rounded-full bg-white/20 backdrop-blur-sm">
              <Heart className="w-7 h-7 text-white" fill="currentColor" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-1">About Partner Nest</h2>
            {/* <p className="text-base text-rose-100">Partner Nest</p> */}
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-8 space-y-8">
          {/* Main Description */}
          <div className="bg-[#0b3b82]/80 rounded-xl p-6 border border-white/10 shadow-2xl">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 mt-1">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#ff2d87] via-[#5f1c7c] to-[#0b3b82] flex items-center justify-center">
                  <span className="text-2xl">💝</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-4">
                  What is Partner Nest?
                </h3>
                <p className="text-lg text-white/80 leading-relaxed mb-4">
                  Partner Nest is <span className="font-semibold text-[#ffd9a6]">India's 1st matrimony platform</span> created with a clear mission:
                  to promote <span className="font-semibold text-[#ffd9a6]">trusted, dignified, and value-based marriages</span>.
                </p>
                <div className="bg-white/10 rounded-lg p-4 border-l-4 border-[#ffd9a6]">
                  <p className="text-lg text-white/90 font-medium italic">
                    "We believe marriage is a lifelong bond — not a financial transaction." 💍
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Core Values */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-[#0b3b82]/80 via-[#5f1c7c]/70 to-[#ff2d87]/40 rounded-xl p-6 shadow-2xl transition-shadow hover:shadow-[0_20px_80px_-40px_rgba(255,45,135,0.45)] border border-[#ffd9a6]/30">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#ff2d87] via-[#5f1c7c] to-[#0b3b82] flex items-center justify-center mb-4 mx-auto">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-lg font-bold text-white text-center mb-2">🛡️ Dignity First</h4>
              <p className="text-white/80 text-center text-sm">
                Every individual deserves respect and dignity in their journey to find a life partner.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#ff2d87]/80 via-[#ffd9a6]/60 to-[#5f1c7c]/70 rounded-xl p-6 shadow-2xl transition-shadow hover:shadow-[0_20px_80px_-40px_rgba(255,45,135,0.45)] border border-[#ffd9a6]/30">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#ffd9a6] to-[#ff2d87] flex items-center justify-center mb-4 mx-auto">
                <Heart className="w-8 h-8 text-[#0b3b82]" fill="currentColor" />
              </div>
              <h4 className="text-lg font-bold text-[#0b3b82] text-center mb-2">❤️ Love & Values</h4>
              <p className="text-[#0b3b82]/80 text-center text-sm">
                Building relationships based on mutual respect, understanding, and shared values.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#5f1c7c]/80 via-[#0b3b82]/85 to-[#ff2d87]/40 rounded-xl p-6 shadow-2xl transition-shadow hover:shadow-[0_20px_80px_-40px_rgba(255,45,135,0.45)] border border-[#ffd9a6]/30">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#0b3b82] via-[#5f1c7c] to-[#ff2d87] flex items-center justify-center mb-4 mx-auto">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-lg font-bold text-white text-center mb-2">🤝 Equality</h4>
              <p className="text-white/80 text-center text-sm">
                Breaking free from traditional systems and creating a society based on equality.
              </p>
            </div>
          </div>

          {/* Our Mission */}
          <div className="bg-gradient-to-r from-[#5f1c7c]/80 via-[#0b3b82]/75 to-[#ff2d87]/45 rounded-xl p-6 border border-[#ffd9a6]/20 shadow-2xl">
            <div className="flex items-center space-x-3 mb-4">
              <Star className="w-8 h-8 text-[#ffd9a6]" fill="currentColor" />
              <h3 className="text-2xl font-bold text-white">Our Mission ⭐</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-[#ffd9a6] flex-shrink-0 mt-1" />
                <p className="text-white/80">Empower individuals to make marriage decisions based on compatibility, not financial pressure</p>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-[#ffd9a6] flex-shrink-0 mt-1" />
                <p className="text-white/80">Create a platform where families connect with mutual respect and understanding</p>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-[#ffd9a6] flex-shrink-0 mt-1" />
                <p className="text-white/80">Build a community that celebrates love and commitment over monetary transactions</p>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-[#ffd9a6] flex-shrink-0 mt-1" />
                <p className="text-white/80">Contribute to a progressive society where marriages are built on equality and mutual respect</p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-[#ff2d87] via-[#5f1c7c] to-[#0b3b82] rounded-xl p-8 text-center shadow-[0_30px_90px_-40px_rgba(0,0,0,0.55)]">
            <Award className="w-16 h-16 text-[#ffd9a6] mx-auto mb-4" />
            <h3 className="text-3xl font-bold text-white mb-3">Join Our Movement 🌟</h3>
            <p className="text-xl text-[#ffd9a6] mb-6">
              Be part of India's revolution for trusted, dignified marriages
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-white text-sm">
              <div className="bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 border border-white/15">
                <span className="font-semibold">💑 Thousands of Success Stories</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 border border-white/15">
                <span className="font-semibold">🌸 100% Verified Profiles</span>
              </div>
            </div>
          </div>

          {/* Footer Quote */}
          <div className="text-center py-4">
            <p className="text-white/70 italic text-sm">
              "True love is not about what you have, but about who you are." 💕
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
